/** Shared MUI Dialog layout: expand/“see more” scrolls inside, modal height stays capped. */

export const DIALOG_MAX_HEIGHT = '85vh';

/**
 * Standard dialog chrome, first shaped on the case and appointment forms.
 *
 * Header and footer stay put while only the middle scrolls, and every dialog gets the same
 * padding, hairlines and mobile behaviour. These live here rather than inline so a dialog added
 * later cannot quietly end up with its own spacing.
 */

/** `PaperProps.sx` — bordered card on desktop, edge-to-edge sheet on mobile. */
export function dialogPaperSx(fullScreen = false) {
  return {
    borderRadius: fullScreen ? 0 : 3,
    border: fullScreen ? 'none' : '1px solid',
    borderColor: 'divider',
    maxHeight: fullScreen ? '100%' : 'calc(100% - 64px)',
  };
}

/** `DialogTitle.sx` — pinned above the scroll area. */
export const dialogTitleSx = {
  flexShrink: 0,
  px: { xs: 2, sm: 2.5 },
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/**
 * Wrapper `sx` for a `<Box component="form">` between the title and the actions.
 * Without it the form is a plain block and the whole dialog scrolls instead of the content.
 */
export const dialogFormSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
};

/** `DialogContent.sx` — the only scrolling region. */
export const dialogContentSx = {
  px: { xs: 2, sm: 2.5 },
  py: 2.5,
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto' as const,
};

/** `DialogActions.sx` — pinned below the scroll area. */
export const dialogActionsSx = {
  flexShrink: 0,
  px: { xs: 2, sm: 2.5 },
  py: 2,
  gap: 1,
  borderTop: '1px solid',
  borderColor: 'divider',
};

/** PaperProps.sx — flex column; full viewport on mobile fullScreen. */
export function dialogPaperScrollSx(fullScreen = false) {
  return {
    maxHeight: fullScreen ? '100%' : DIALOG_MAX_HEIGHT,
    height: fullScreen ? '100%' : undefined,
    display: 'flex',
    flexDirection: 'column' as const,
    borderRadius: fullScreen ? 0 : undefined,
  };
}

/**
 * DialogContent sx — scrolls when “Arată mai mult” expands text.
 * Header (~4rem) + footer (~4rem) + margins ≈ 11rem reserved (desktop popup only).
 */
export function dialogContentScrollSx(fullScreen = false) {
  return {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto' as const,
    ...(fullScreen
      ? {}
      : { maxHeight: `calc(${DIALOG_MAX_HEIGHT} - 11rem)` }),
  };
}
